import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { Icon } from "@/components/Icon";
import { BomNode } from "@/features/job-tracking/queries/tracking-data/bom-hierarchy-generator";
import { withJobTrackingData } from "../job-tracking/components/withJobTrackingData";
import { BomComponent } from "../job-tracking/queries/tracking-data/bom-by-component";
import { EffectivePriority } from "./purchasing-priority-utils";

export const BomNodePurchasingPriorityIcon: React.FC<BomNode> = withJobTrackingData(
  ({ bomComponentsByItemId, purchasingPriority, recursiveDepth }) => {
    // Determine icon and style based on priority sourceType/inherited
    const { appliedRecord, appliedRecordSource, inherited } = purchasingPriority;
    const icon =
      appliedRecordSource === "default" ? "slash-circle"
      : inherited ? "link"
      : appliedRecordSource === "job" ? "star-fill"
      : "file-earmark-text"; // template override
    const iconColorClass = appliedRecordSource === "job" || !!appliedRecord?.jobId ? "text-primary" : null;
    const fontStyleClass =
      appliedRecordSource === "default" ? "fst-italic"
      : !inherited ? "fw-bold"
      : null;

    return (
      <OverlayTrigger
        overlay={
          <Tooltip>
            <TooltipText {...{ purchasingPriority, recursiveDepth, bomComponentsByItemId }} />
          </Tooltip>
        }
      >
        <span className={classNames(iconColorClass, fontStyleClass)}>
          <Icon icon={icon} width={18} height={18} />
        </span>
      </OverlayTrigger>
    );
  },
);

const TooltipText: React.FC<{
  purchasingPriority: EffectivePriority;
  recursiveDepth: number;
  bomComponentsByItemId: Map<number, BomComponent>;
}> = ({
  purchasingPriority: { appliedRecord, appliedRecordSource, inherited },
  recursiveDepth,
  bomComponentsByItemId,
}) => {
  if (appliedRecordSource === "default") return "Default Priority";

  if (!inherited) {
    const setText = recursiveDepth === 0 ? "Set" : "Overridden";
    return appliedRecordSource === "job" ? `${setText} for this job` : `${setText} in Template`;
  }

  const inheritedFromItem = bomComponentsByItemId.get(appliedRecord!.targetItemId)!.item;
  const distanceFromInherited = recursiveDepth - appliedRecord!.pathSegments.length + 1;
  const levels = distanceFromInherited === 1 ? "level" : "levels";
  return (
    <>
      Inherited from {inheritedFromItem.name} ({distanceFromInherited.toFixed()} {levels} up)
    </>
  );
};
